// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import Carousel from 'react-multi-carousel';
import { arrayOf, bool, number, shape, string } from 'prop-types';

import ProductGridItem from 'Containers/Products/ProductGridItem';
import useTemplateProducts from './useTemplateProducts';

const StyledProductGridSlider = styled.section`
  box-sizing: border-box;
  position: relative;
  width: 100%;

  &.withDots {
    padding-bottom: 2rem;
  }

  .productGridCarousel {
    margin-inline: calc(var(--product-slider-gap) / -2);
    width: calc(100% + var(--product-slider-gap));
  }

  .productGridItem {
    align-items: stretch;
    padding-inline: calc(var(--product-slider-gap) / 2);
  }

  .productGridSlide {
    display: flex;
    min-width: 0;
    width: 100%;
  }

  .productGridDots {
    bottom: 0;
  }

  .react-multiple-carousel__arrow {
    z-index: 5;
  }
`;

const State = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-height: 180px;
  padding: 24px;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  background: #f8fafc;
`;

const itemCount = value => Math.max(1, Math.floor(value || 1));

const ProductGridSlider = ({
  selections,
  desktopItems,
  tabletItems,
  mobileItems,
  gap,
  showArrows,
  showDots,
  autoPlay,
  autoPlaySpeed,
}) => {
  const productIds = selections.map(({ productId }) => productId).filter(Boolean);
  const { products, loading, error } = useTemplateProducts(productIds);

  if (!productIds.length) return <State>Add at least one product.</State>;
  if (loading) return <State>Loading products…</State>;
  if (error) return <State>Could not load products.</State>;
  if (!products.length) return <State>Products not found.</State>;

  const responsive = {
    desktop: {
      breakpoint: { max: 5000, min: 1368 },
      items: itemCount(desktopItems),
    },
    tablet: {
      breakpoint: { max: 1368, min: 767 },
      items: itemCount(tabletItems),
    },
    mobile: {
      breakpoint: { max: 767, min: 0 },
      items: itemCount(mobileItems),
    },
  };
  const selectionIds = selections.reduce((idsByProduct, selection) => {
    if (!selection.productId) return idsByProduct;
    const keys = idsByProduct.get(selection.productId) || [];
    keys.push(selection.id);
    idsByProduct.set(selection.productId, keys);
    return idsByProduct;
  }, new Map());

  return (
    <StyledProductGridSlider
      className={ showDots ? 'withDots' : '' }
      style={ { '--product-slider-gap': `${Math.max(0, gap || 0)}px` } }
      aria-label="Product slider"
    >
      <Carousel
        arrows={ showArrows }
        autoPlay={ autoPlay }
        autoPlaySpeed={ Math.max(1000, autoPlaySpeed || 3000) }
        className="productGridCarousel"
        deviceType="desktop"
        dotListClass="productGridDots"
        draggable
        itemClass="productGridItem"
        keyBoardControl
        pauseOnHover
        renderDotsOutside={ showDots }
        responsive={ responsive }
        shouldResetAutoplay
        showDots={ showDots }
        slidesToSlide={ 1 }
        ssr
        swipeable
      >
        {products.map(product => (
          <div
            className="productGridSlide"
            key={ selectionIds.get(product.id)?.shift() || product.id }
          >
            <ProductGridItem product={ product } hideQuickView />
          </div>
        ))}
      </Carousel>
    </StyledProductGridSlider>
  );
};

ProductGridSlider.propTypes = {
  selections: arrayOf(shape({
    id: string,
    productId: string,
  })),
  desktopItems: number,
  tabletItems: number,
  mobileItems: number,
  gap: number,
  showArrows: bool,
  showDots: bool,
  autoPlay: bool,
  autoPlaySpeed: number,
};

export default ProductGridSlider;
